import { useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { GeoJSON, Marker, Pane, Polyline, Popup, Tooltip } from "react-leaflet";
import { divIcon, DomEvent } from "leaflet";

import { Button, Card, Dialog, DialogTitle, Stack, Typography } from "@mui/material";

import { MAP_CONTENTS_OPACITY, ROUTE_SIZE, STATIONS_COLOR, STATIONS_SIZE, SMALL_STATIONS_SIZE } from "Configs/map";

import { useAppDispatch, useAppSelector } from "Hooks/useRedux";

import { STATIONS2 } from "Constants/Stations";
import { Box } from "@mui/system";
import { changeShowPaths, changeShowReportRightControl } from "Redux/sideSlice";
import useTrafficPath from "Hooks/useTrafficPath";

const ICON_SIZE = 22;

const Colors = ["#ec1c26", "#27388d", "#04a9f2", "#fddf17", "#099146", "#f26cab", "#833e9d"];

export default function TrafficLinePath() {
  const { visible, pathShowType, selectedPath } = useAppSelector((state) => state.trafficPath);
  const { pathLayer } = useAppSelector((state) => state.layers);

  const dispatch = useAppDispatch();

  const { data } = useTrafficPath();

  const curvePolyline = useMemo(() => {
    if (!data) return null;

    let finalData;

    if (selectedPath.length > 0) {
      finalData = selectedPath;
    } else {
      finalData = data;
    }

    return finalData.map((p: any) => {
      let path: any;
      if (selectedPath.length > 0) {
        path = p;
      } else {
        if (!p.Routes) return null;
        path = JSON.parse(`{${p.Routes}}`);
      }

      if (!path || path.stations.length === 0) return null;
      const { stations } = path;
      const pathColor = Colors[Number(path.code) - 1];

      const events = {
        click: () => {
          dispatch(changeShowPaths(false));
        },
      };
      const stationsLength = path.stations.length - 1;
      const markers: any = [];
      const positions: any = path.stations.map((station: any, indx: number) => {
        const tooltip = (
          <Tooltip direction="top" offset={[0, -20]}>
            <Card sx={{ py: 2, px: 3 }}>
              <Stack alignItems="center">
                <Typography>{stations[indx].title}</Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  کد ایستگاه : {stations[indx].code}
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  شماره خط : {path?.code}
                </Typography>
              </Stack>
            </Card>
          </Tooltip>
        );

        const setBorderColor = (n: number) => {
          switch (n) {
            case 0:
              return pathColor;

            case stationsLength:
              return "#ffffff00";

            default:
              return pathColor;
          }
        };
        const stationData = STATIONS2.filter((i) => i.type === "metro")[0];
        const finalSize = indx === 0 || indx === stationsLength ? STATIONS_SIZE : SMALL_STATIONS_SIZE;
        const iconMarkup = renderToStaticMarkup(
          <Box
            style={{
              width: indx === stationsLength ? "45px" : `${finalSize}px`,
              height: indx === stationsLength ? "50px" : `${finalSize}px`,
              backgroundColor: indx === stationsLength ? "#ffffff00" : "#fff",
              borderRadius: indx === stationsLength ? "none" : "50%",
              border: "2px solid",
              borderColor: setBorderColor(indx),
              display: pathShowType.includes(6) || indx === stationsLength || indx === 0 ? "grid" : "none",
              placeItems: "center",
              color: pathColor,
            }}
          >
            {indx === stationsLength ? (
              <svg
                width={65}
                height={65}
                viewBox="7 2 15 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ paddingBottom: "20px" }}
              >
                <path
                  d="M7.96891 4.24999C9.31606 1.91666 12.6839 1.91666 14.0311 4.25L18.3612 11.75C19.7084 14.0833 18.0244 17 15.3301 17H6.66986C3.97557 17 2.29163 14.0833 3.63878 11.75L7.96891 4.24999Z"
                  fill="#fff"
                  stroke={pathColor}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
            {indx === 0 || indx === stationsLength ? stationData.icon : null}
          </Box>
        );
        const icon = divIcon({
          html: iconMarkup,
          iconAnchor: [finalSize / 2, finalSize / 2],
        });
        markers.push(
          <Marker key={station.id} position={[station.lat, station.lng]} icon={icon} zIndexOffset={1}>
            {tooltip}
          </Marker>
        );
        return [station.lat, station.lng];
      });

      return (
        <>
          {markers}
          <Polyline positions={positions} pathOptions={{ color: pathColor, weight: 4 }} eventHandlers={events} />
        </>
      );
    });
  }, [data, pathShowType, selectedPath]);

  return (
    <>
      {pathShowType.includes(5) && visible && curvePolyline}
      <Pane name="traffic-path-line" style={{ zIndex: pathLayer }} key={pathLayer}>
        {pathShowType.includes(5) && visible && curvePolyline}
      </Pane>
    </>
  );
}
